/**
 * TrabajosEspecialesModule.jsx
 * Módulo de captura de Trabajos Especiales (Fontanería · Herrería).
 * Ruta: /admin/trabajos-especiales?dep=FONTANERIA | HERRERIA
 *
 * Octavio 29-ago-2026.
 */

import { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "../auth/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DEP_CONFIG = {
  FONTANERIA: {
    label: "Fontanería",
    icon: "fa-faucet-drip",
    color: "#0F766E",   // teal-700
    bg: "#F0FDFA",
  },
  HERRERIA: {
    label: "Herrería y Pintura",
    icon: "fa-screwdriver-wrench",
    color: "#B45309",   // amber-700
    bg: "#FFFBEB",
  },
};

// ── Formulario de captura ─────────────────────────────────────────────────────
function FormCaptura({ dep, onGuardado }) {
  const cfg = DEP_CONFIG[dep] || DEP_CONFIG.FONTANERIA;
  const hoy = new Date().toISOString().slice(0, 10);

  const [form, setForm] = useState({
    fecha:        hoy,
    no_empleado:  "",
    nombre:       "",
    unidad:       "",
    actividad:    "",
    ubicacion:    "",
    pintura_l:    "",
    soldadura_kg: "",
    thinner_l:    "",
    gasolina_l:   "",
  });
  const [fotos, setFotos]     = useState([]);
  const [saving, setSaving]   = useState(false);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.actividad.trim()) {
      toast.error("El campo Actividad es obligatorio");
      return;
    }
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("departamento", dep);
      Object.entries(form).forEach(([k, v]) => {
        if (v !== "") fd.append(k, v);
      });
      fotos.forEach(f => fd.append("fotos", f));

      await axios.post(`${API}/admin/trabajos-especiales`, fd, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Trabajo registrado ✓");
      setForm({ ...form, actividad: "", ubicacion: "", pintura_l: "", soldadura_kg: "", thinner_l: "", gasolina_l: "" });
      setFotos([]);
      onGuardado?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  const inputCls = "w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500";
  const labelCls = "block text-xs font-medium text-slate-600 mb-1";

  return (
    <form onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-xl flex items-center justify-center text-white text-lg"
             style={{ background: cfg.color }}>
          <i className={`fa-solid ${cfg.icon}`} />
        </div>
        <div>
          <h2 className="text-lg font-bold text-slate-900">Nuevo registro — {cfg.label}</h2>
          <p className="text-xs text-slate-500">Completa los campos y guarda</p>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
        <div>
          <label className={labelCls}>Fecha *</label>
          <input type="date" className={inputCls} value={form.fecha}
            onChange={e => set("fecha", e.target.value)} required />
        </div>
        <div>
          <label className={labelCls}>No. Empleado</label>
          <input type="text" className={inputCls} value={form.no_empleado}
            placeholder="Ej. 1234"
            onChange={e => set("no_empleado", e.target.value)} />
        </div>
        <div>
          <label className={labelCls}>Nombre del empleado</label>
          <input type="text" className={inputCls} value={form.nombre}
            placeholder="Nombre completo"
            onChange={e => set("nombre", e.target.value)} />
        </div>
        <div>
          <label className={labelCls}>Unidad / No. Económico</label>
          <input type="text" className={inputCls} value={form.unidad}
            placeholder="Ej. 301"
            onChange={e => set("unidad", e.target.value)} />
        </div>
        <div className="sm:col-span-2">
          <label className={labelCls}>Actividad *</label>
          <input type="text" className={inputCls} value={form.actividad}
            placeholder="Descripción del trabajo realizado"
            onChange={e => set("actividad", e.target.value)} required />
        </div>
        <div className="sm:col-span-2 lg:col-span-3">
          <label className={labelCls}>Ubicación / Colonia</label>
          <input type="text" className={inputCls} value={form.ubicacion}
            placeholder="Dirección o colonia donde se realizó el trabajo"
            onChange={e => set("ubicacion", e.target.value)} />
        </div>
      </div>

      {/* Materiales */}
      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3 mt-2">Materiales utilizados (opcional)</p>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-4">
        {dep === "FONTANERIA" && (
          <>
            <div>
              <label className={labelCls}>Pintura (litros)</label>
              <input type="number" step="0.1" min="0" className={inputCls} value={form.pintura_l}
                onChange={e => set("pintura_l", e.target.value)} />
            </div>
            <div>
              <label className={labelCls}>Thinner (litros)</label>
              <input type="number" step="0.1" min="0" className={inputCls} value={form.thinner_l}
                onChange={e => set("thinner_l", e.target.value)} />
            </div>
          </>
        )}
        {dep === "HERRERIA" && (
          <>
            <div>
              <label className={labelCls}>Soldadura (kg)</label>
              <input type="number" step="0.1" min="0" className={inputCls} value={form.soldadura_kg}
                onChange={e => set("soldadura_kg", e.target.value)} />
            </div>
            <div>
              <label className={labelCls}>Pintura (litros)</label>
              <input type="number" step="0.1" min="0" className={inputCls} value={form.pintura_l}
                onChange={e => set("pintura_l", e.target.value)} />
            </div>
          </>
        )}
        <div>
          <label className={labelCls}>Gasolina (litros)</label>
          <input type="number" step="0.1" min="0" className={inputCls} value={form.gasolina_l}
            onChange={e => set("gasolina_l", e.target.value)} />
        </div>
      </div>

      {/* Fotos */}
      <div className="mb-6">
        <label className={labelCls}>Fotos (opcional)</label>
        <input
          type="file" multiple accept="image/*"
          className="block text-sm text-slate-600 file:mr-3 file:py-1 file:px-3 file:rounded-lg file:border-0 file:text-xs file:font-medium file:bg-slate-100 file:text-slate-700 hover:file:bg-slate-200"
          onChange={e => setFotos(Array.from(e.target.files))}
        />
        {fotos.length > 0 && (
          <p className="text-xs text-slate-500 mt-1">{fotos.length} foto(s) seleccionada(s)</p>
        )}
      </div>

      <button
        type="submit"
        disabled={saving}
        className="px-6 py-2.5 rounded-xl text-white text-sm font-medium disabled:opacity-60"
        style={{ background: cfg.color }}
      >
        {saving ? <><i className="fa-solid fa-spinner fa-spin mr-2" />Guardando…</> : <><i className="fa-solid fa-floppy-disk mr-2" />Guardar registro</>}
      </button>
    </form>
  );
}

// ── Historial reciente ────────────────────────────────────────────────────────
function TablaHistorial({ dep, refreshKey }) {
  const [datos, setDatos]   = useState([]);
  const [total, setTotal]   = useState(0);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/admin/trabajos-especiales`, {
      params: { departamento: dep, limit: 50 },
      withCredentials: true,
    }).then(r => {
      setDatos(r.data.datos || []);
      setTotal(r.data.total || 0);
    }).catch(() => {}).finally(() => setLoading(false));
  }, [dep, refreshKey]);

  if (loading) return <div className="text-center py-8 text-slate-400"><i className="fa-solid fa-spinner fa-spin mr-2" />Cargando…</div>;
  if (!datos.length) return <div className="text-center py-8 text-slate-400">Sin registros recientes.</div>;

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden mt-6">
      <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
        <h3 className="font-semibold text-slate-800 text-sm">Registros recientes</h3>
        <span className="text-xs text-slate-500">Mostrando {datos.length} de {total}</span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="bg-slate-50">
            <tr>
              {["Fecha","Empleado","Unidad","Actividad","Ubicación","Mat.","Fotos"].map(h => (
                <th key={h} className="px-4 py-2 text-left font-semibold text-slate-600">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {datos.map((d, i) => (
              <tr key={d.id || i} className="border-t border-slate-50 hover:bg-slate-50/60">
                <td className="px-4 py-2 whitespace-nowrap">{d.fecha || "—"}</td>
                <td className="px-4 py-2">
                  <div className="font-medium">{d.no_empleado || "—"}</div>
                  {d.nombre && <div className="text-slate-400">{d.nombre}</div>}
                </td>
                <td className="px-4 py-2">{d.unidad || "—"}</td>
                <td className="px-4 py-2 max-w-xs truncate">{d.actividad}</td>
                <td className="px-4 py-2 max-w-[140px] truncate">{d.ubicacion || "—"}</td>
                <td className="px-4 py-2 whitespace-nowrap">
                  {[
                    d.pintura_l    && `P:${d.pintura_l}L`,
                    d.soldadura_kg && `S:${d.soldadura_kg}kg`,
                    d.thinner_l    && `T:${d.thinner_l}L`,
                    d.gasolina_l   && `G:${d.gasolina_l}L`,
                  ].filter(Boolean).join(" · ") || "—"}
                </td>
                <td className="px-4 py-2">
                  {(d.fotos?.length || 0) > 0
                    ? <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-teal-50 text-teal-700 font-medium">
                        <i className="fa-solid fa-image text-[10px]" />{d.fotos.length}
                      </span>
                    : <span className="text-slate-300">—</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Módulo principal ──────────────────────────────────────────────────────────
export default function TrabajosEspecialesModule() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const dep = (searchParams.get("dep") || "FONTANERIA").toUpperCase();
  const [refreshKey, setRefreshKey] = useState(0);

  const cfg = DEP_CONFIG[dep] || DEP_CONFIG.FONTANERIA;

  // Redirigir si el departamento no es válido
  useEffect(() => {
    if (!DEP_CONFIG[dep]) navigate("/admin/trabajos-especiales?dep=FONTANERIA", { replace: true });
  }, [dep, navigate]);

  return (
    <div className="min-h-screen bg-slate-50 px-4 py-6 max-w-5xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <button onClick={() => navigate("/hub")}
          className="text-slate-400 hover:text-slate-600 text-xl">
          <i className="fa-solid fa-arrow-left" />
        </button>
        <div className="w-12 h-12 rounded-2xl flex items-center justify-center text-white text-xl shadow"
             style={{ background: cfg.color }}>
          <i className={`fa-solid ${cfg.icon}`} />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-slate-900">{cfg.label}</h1>
          <p className="text-sm text-slate-500">Registro de trabajos especiales · DMU 2026</p>
        </div>

        {/* Selector de departamento */}
        <div className="ml-auto flex gap-2">
          {Object.entries(DEP_CONFIG).map(([key, c]) => (
            <button
              key={key}
              onClick={() => navigate(`/admin/trabajos-especiales?dep=${key}`)}
              className="px-3 py-1.5 rounded-lg text-xs font-medium border transition-all"
              style={dep === key
                ? { background: c.color, color: "#fff", borderColor: c.color }
                : { background: "#fff", color: "#475569", borderColor: "#e2e8f0" }}
            >
              <i className={`fa-solid ${c.icon} mr-1`} />{c.label}
            </button>
          ))}
        </div>
      </div>

      {/* Formulario */}
      <FormCaptura dep={dep} onGuardado={() => setRefreshKey(k => k + 1)} />

      {/* Historial */}
      <TablaHistorial dep={dep} refreshKey={refreshKey} />
    </div>
  );
}
